import React from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../../Catalyst/dialog";
import { Button } from "../../Catalyst/button";
import { Badge } from "../../Catalyst/badge";
import { NotificationItem } from "../../../store/slices/NotificationsSlice";
import { NOTIFICATION_STATUS_BADGE } from "../../../constants/notifications";
import {
  formatDateTime,
  getNotificationType,
  getNotificationUserName,
  getPayloadMessage,
  stringifyPayload,
} from "./utils";

interface NotificationDetailsDialogProps {
  notification: NotificationItem | null;
  open: boolean;
  onClose: () => void;
}

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</span>
    <span className="text-sm text-texthead break-words">{value || "—"}</span>
  </div>
);

const NotificationDetailsDialog = ({ notification, open, onClose }: NotificationDetailsDialogProps) => {
  if (!notification) return null;

  const statusConfig = NOTIFICATION_STATUS_BADGE[notification.status];
  const { title, body } = getPayloadMessage(notification.payload);

  return (
    <Dialog size="2xl" open={open} onClose={onClose}>
      <div className="flex items-start justify-between gap-4">
        <DialogTitle>Notification details</DialogTitle>
        <Badge color={statusConfig?.color ?? "zinc"}>
          {statusConfig?.label ?? notification.status}
        </Badge>
      </div>

      <DialogBody>
        <div className="grid grid-cols-2 gap-4">
          <DetailRow label="User" value={getNotificationUserName(notification)} />
          <DetailRow label="Type" value={getNotificationType(notification)} />
          <DetailRow label="Rule" value={notification.ruleName || notification.ruleKey} />
          <DetailRow label="Trigger" value={notification.triggerType} />
          <DetailRow label="Channel" value={notification.channel} />
          <DetailRow label="Created" value={formatDateTime(notification.createdAt)} />
          <DetailRow label="Sent" value={formatDateTime(notification.sentAt)} />
        </div>

        {(title || body) && (
          <div className="mt-6 rounded-lg border border-shadowborder bg-[#F9FAFB] p-4">
            {title && <div className="text-sm font-semibold text-texthead">{title}</div>}
            {body && <div className="mt-1 text-sm text-textColor">{body}</div>}
          </div>
        )}

        <div className="mt-6">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
            Raw payload
          </div>
          <pre className="max-h-[300px] overflow-auto rounded-lg border border-shadowborder bg-[#F9FAFB] p-4 text-xs text-textColor whitespace-pre-wrap break-words">
            {stringifyPayload(notification.payload)}
          </pre>
        </div>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationDetailsDialog;
